import { ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { getPageRange } from '@/lib/list/pagination';
import { cn } from '@/lib/utils';

/**
 * Offset pagination for every card list (spec R6.5, R6.8).
 *
 * Two arrows and a count, not a numbered strip: with 24 cards a page, a project
 * list runs to a handful of pages and a row of numbers would be more chrome
 * than navigation. The count is the part that earns its place — it says how
 * much is behind the filter without making anyone page to the end to find out.
 */
export interface PaginationBarProps {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  /** What is being counted, lowercase and plural: "projects". */
  itemNoun: string;
  className?: string;
}

export const PaginationBar = ({
  page,
  pageSize,
  total,
  onPageChange,
  itemNoun,
  className,
}: PaginationBarProps) => {
  const { from, to, pageCount, hasPrevious, hasNext } = getPageRange(
    page,
    pageSize,
    total,
  );

  if (total === 0) {
    return null;
  }

  return (
    <nav
      aria-label={`${itemNoun} pagination`}
      className={cn(
        'flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between',
        className,
      )}
    >
      <p className="text-xs text-muted-foreground" aria-live="polite">
        {from === 0
          ? `No ${itemNoun} on this page of ${String(total)}`
          : `Showing ${String(from)}–${String(to)} of ${String(total)} ${itemNoun}`}
      </p>

      {pageCount > 1 ? (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!hasPrevious}
            onClick={() => {
              onPageChange(page - 1);
            }}
          >
            <ChevronLeft aria-hidden="true" />
            Previous
          </Button>
          <span className="px-1 text-xs tabular-nums text-muted-foreground">
            Page {page} of {pageCount}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={!hasNext}
            onClick={() => {
              onPageChange(page + 1);
            }}
          >
            Next
            <ChevronRight aria-hidden="true" />
          </Button>
        </div>
      ) : null}
    </nav>
  );
};
